<script setup>
/* global axios */
import { computed, nextTick, onMounted, onUnmounted, reactive, ref, watch } from 'vue';
import { isNavigationFailure, useRoute, useRouter } from 'vue-router';
import { useMapGetter } from 'dashboard/composables/store';
import { emitter } from 'shared/helpers/mitt';
import { attentionEnabled, attentionState, countLabel, messageIsPresented, waitingAge } from './state';
import TabAttention from './TabAttention';
import WorkstationLease from './WorkstationLease';
import { recoverAccountAttention } from './accountRecovery';
import { flushAttentionDrafts } from './draftFlush';
import { capabilityState, commitCapabilities } from './capabilities';

const route = useRoute();
const router = useRouter();
// Upstream conversation/inbox/label routes use conversation_id; the commerce
// skin and team routes use conversationId. Normalize only known route fields.
const displayedConversationId = computed(() => {
  const id = Number(route.params.conversation_id ?? route.params.conversationId);
  return Number.isSafeInteger(id) && id > 0 ? id : null;
});
const currentUser = useMapGetter('getCurrentUser');
const expanded = ref(false);
const healthOpen = ref(false);
const attentionElement = ref(null);
const status = ref('Connecting');
const error = ref('');
// A successful transport sync cannot certify a failed user action.
const actionErrors = reactive({});
const actionAttempts = new Map();
const failedActions = new Map();
let actionSequence = 0;
const soundStatus = ref('Untested');
const nativeStatus = ref('Untested');
const badgeStatus = ref('Unavailable');
const companion = ref('Unavailable in this browser');
const lastSync = ref(null);
const lastPong = ref(null);
const storageStatus = ref('Unknown');
const announcement = ref('');
const now = ref(Date.now());
const permission = ref('Notification' in window ? Notification.permission : 'unavailable');
const snapshot = computed(() => attentionState.snapshot);
const counts = computed(() => snapshot.value?.counts || {});
const items = computed(() => snapshot.value?.items || []);
const preferences = computed(() => snapshot.value?.preferences || { sound: true, native: true, reminders: true, volume: 0.65 });
const namespace = computed(() => snapshot.value ? `attention:${snapshot.value.deployment_id}:${snapshot.value.actor_id}` : null);
const recoveredAccount = ref(null);
const requestAccount = () => {
  const routeAccount = Number(route.params.accountId);
  const enabled = capabilityState.value?.actor_id === currentUser.value?.id ? capabilityState.value.account_ids : null;
  return recoveredAccount.value || (enabled?.length && !enabled.includes(routeAccount) ? enabled[0] : routeAccount);
};
const endpoint = () => `/api/v1/accounts/${requestAccount()}/attention`;
const socketStatus = computed(() => lastPong.value && now.value - lastPong.value < 12000 ? 'Freshness verified' : 'Unknown or stale; reconciliation active');
const oldest = computed(() => waitingAge(counts.value.oldest_waiting_at, now.value));

let generation = 0; let timer; let pending = false; let resyncRequested = false; let disposed = false; let requestAbort;
let tabAttention; let lease; let lastNamespace; let probe;
let scopeRetryAt = 0;
let scopeCursor = {};
let badgePending = false; let badgeTarget;
let sound;
let registration;
let pushReady = false; let pushRenewAt = 0; let pushRegistering = false; let pushEpoch = 0;
let pushClearing = 0;
let pushIdentity;
let pushDisabledState;
let pushAccounts = '';
let deviceId;
let readTimer; let readKey; let toastTimer;
let nativeNavigationCleanup;
const nativeNavigations = new Set();
const nativeNavigationInFlight = new Set();
const lastObserved = new Map();
const readReceipts = new Set();
const reminders = new Map();
const reported = new Set();

function togglePanel(panel) {
  if (panel === 'work') { expanded.value = !expanded.value; healthOpen.value = false; }
  else { healthOpen.value = !healthOpen.value; expanded.value = false; }
}
function closePanels(event) {
  const element = attentionElement.value;
  if (event && !element?.contains(event.target)) return;
  const restore = element?.contains(document.activeElement);
  const control = healthOpen.value ? '[aria-controls="attention-health"]' : '.attention-summary';
  expanded.value = false; healthOpen.value = false;
  if (restore) element.querySelector(control)?.focus();
}

function diagnosticScope() {
  return { epoch: generation, actor: currentUser.value?.id, account: requestAccount(), endpoint: endpoint() };
}
function report(reason, result = 'failed', scope = diagnosticScope()) {
  const code = String(reason).replace(/[^a-zA-Z0-9_:.-]/g, '_').slice(0, 80);
  if (reported.has(code) || reported.size >= 20) return;
  reported.add(code);
  Promise.resolve().then(() => {
    if (disposed || scope.epoch !== generation || scope.actor !== currentUser.value?.id || scope.account !== requestAccount()) return;
    return axios.post(`${scope.endpoint}/report`, { stage: 'adapter', result, reason: code });
  }).catch(() => {});
}
function safeDeviceId() {
  try {
    let id = localStorage.getItem('reliable-attention-device');
    if (!id || !/^[a-zA-Z0-9_-]{16,80}$/.test(id)) { id = crypto.randomUUID(); localStorage.setItem('reliable-attention-device', id); }
    return id;
  } catch { storageStatus.value = 'Unavailable; counts still reconcile'; return crypto.randomUUID(); }
}
function tone(kind) {
  return ['escalation', 'backup', 'handoff', 'assignment'].includes(kind) ? 'bell' : 'ding';
}
function captureAttentionScope() {
  return { epoch: generation, actor: currentUser.value?.id, namespace: namespace.value, lease };
}
function currentAttentionScope(scope) {
  return !disposed && scope.epoch === generation && scope.actor === currentUser.value?.id &&
    snapshot.value?.actor_id === scope.actor && namespace.value === scope.namespace &&
    scope.lease === lease && !!scope.lease && !scope.lease.disposed;
}
async function play(kind, test = false, scope = captureAttentionScope()) {
  if (!currentAttentionScope(scope)) return;
  if (!test && !preferences.value.sound) return;
  try {
    if (!sound || sound.dataset.tone !== tone(kind)) {
      sound?.pause();
      sound = new Audio(`/audio/dashboard/${tone(kind)}.mp3`);
      sound.dataset.tone = tone(kind);
      sound.preload = 'auto';
    }
    sound.volume = preferences.value.volume;
    sound.currentTime = 0;
    await sound.play();
    if (!currentAttentionScope(scope)) return;
    soundStatus.value = 'Browser accepted playback; OS output untested';
  } catch (failure) {
    if (!currentAttentionScope(scope)) return;
    soundStatus.value = failure.name === 'NotAllowedError' ? 'Blocked: use Enable/test sound' : `Unavailable: ${failure.name}`;
    report(`sound:${failure.name}`);
  }
}
function enableSound() {
  // Start playback during the user gesture, before awaiting preference storage.
  play('new_chat', true);
  setPreference('sound', true);
}
function beginAction(category, target) {
  const attempt = { category, target, sequence: ++actionSequence, epoch: generation, actor: currentUser.value?.id };
  actionAttempts.set(category, attempt);
  return attempt;
}
function currentAction(attempt, expectedNavigation = false) {
  return !disposed && currentUser.value?.id === attempt.actor &&
    (expectedNavigation || attempt.epoch === generation) && actionAttempts.get(attempt.category) === attempt;
}
function actionFailed(attempt, message) {
  if (!currentAction(attempt)) return;
  failedActions.set(attempt.category, attempt);
  actionErrors[attempt.category] = message;
}
function actionSucceeded(attempt, expectedNavigation = false) {
  if (!currentAction(attempt, expectedNavigation)) return false;
  // An unrelated successful action cannot certify a failed target was saved.
  if (failedActions.get(attempt.category)?.target === attempt.target) {
    failedActions.delete(attempt.category);
    delete actionErrors[attempt.category];
  }
  return true;
}
async function setPreference(key, value) {
  const attempt = beginAction(`preference:${key}`, key);
  try {
    const { data } = await axios.put(`${endpoint()}/preferences`, { preferences: { [key]: value } });
    if (!actionSucceeded(attempt)) return;
    if (snapshot.value) snapshot.value.preferences = data;
    if (key === 'native') { if (!data.native) await clearPush(); else if (permission.value === 'granted') registerPush(); }
  } catch { actionFailed(attempt, 'Could not save notification preference'); }
}
function isPresent(item) {
  if (item.account_id !== Number(route.params.accountId)) return false;
  if (displayedConversationId.value !== item.conversation_id) return false;
  return messageIsPresented(document.querySelector(`[data-message-id="${item.latest_customer_message_id}"]`));
}
function scheduleVisibleReceipt() {
  const item = items.value.find(row => (row.unseen || row.personal_unseen_messages > 0) && row.visible_receipt_token && isPresent(row));
  const key = item ? `${namespace.value}:${item.stream_id}:${item.version}:${item.latest_customer_message_id}:${item.read_revision || 0}` : null;
  if (!key || readReceipts.has(key)) { clearTimeout(readTimer); readKey = null; return; }
  if (readKey === key) return;
  clearTimeout(readTimer); readKey = key;
  const epoch = generation;
  readTimer = setTimeout(async () => {
    readKey = null;
    if (epoch !== generation || !isPresent(item)) return;
    const current = items.value.find(row => row.stream_id === item.stream_id);
    if (current?.version !== item.version) return;
    const attempt = beginAction('read', `${item.account_id}:${item.conversation_id}`);
    try {
      await axios.post(`${endpoint()}/acknowledge`, { mode: 'visible', receipt_token: item.visible_receipt_token });
      if (!actionSucceeded(attempt)) return;
      readReceipts.add(key);
      if (readReceipts.size > 256) readReceipts.delete(readReceipts.values().next().value);
      sync();
    } catch { actionFailed(attempt, 'Message read state could not be saved'); }
  }, 1200);
}
async function acknowledge(item) {
  const attempt = beginAction('acknowledge', `${item.account_id}:${item.stream_id}`);
  try {
    await axios.post(`${endpoint()}/acknowledge`, { mode: 'explicit', receipt_token: item.receipt_token });
    if (!actionSucceeded(attempt)) return;
    await sync();
  } catch { actionFailed(attempt, 'Acknowledgment was not saved; waiting work remains'); }
}
async function open(item) {
  const sourceRoute = route.fullPath;
  const attempt = beginAction('navigation', `${item.account_id}:${item.conversation_id}`);
  const saved = await flushAttentionDrafts();
  if (!currentAction(attempt) || route.fullPath !== sourceRoute) return false;
  if (!saved) { actionFailed(attempt, 'Could not preserve the current draft or pending attachments. Keep this conversation open.'); return false; }
  if (snapshot.value?.actor_id !== attempt.actor || ![snapshot.value.account_id, ...snapshot.value.background_accounts.map(account => account.account_id)].includes(item.account_id)) {
    actionFailed(attempt, 'This account is no longer in your authorized attention scope. Refresh work or sign in.'); return false;
  }
  const customSkin = router.hasRoute('intelichat_inbox') && route.path.includes('/intelichat/');
  const path = item.queue_notice === true ? `/app/accounts/${item.account_id}/${customSkin ? 'intelichat/inbox' : 'dashboard'}` :
    (customSkin ? `/app/accounts/${item.account_id}/intelichat/inbox/${item.conversation_id}` : `/app/accounts/${item.account_id}/conversations/${item.conversation_id}`);
  try {
    if (route.path === path) return actionSucceeded(attempt);
    const failure = await router.push(path);
    // The requested cross-account route intentionally advances generation.
    // Actor, exact target path and navigation result certify completion.
    if (!currentAction(attempt, true)) return false;
    const navigated = !isNavigationFailure(failure) && route.path === path;
    if (navigated) actionSucceeded(attempt, true);
    else actionFailed(attempt, 'Conversation navigation failed. Waiting work remains.');
    return navigated;
  } catch { actionFailed(attempt, 'Conversation navigation failed. Waiting work remains.'); return false; }
}
async function take(item) {
  const attempt = beginAction('take', `${item.account_id}:${item.conversation_id}`);
  try {
    await axios.post(`${endpoint()}/take`, { conversation_id: item.conversation_id });
    if (!actionSucceeded(attempt)) return;
    open(item); sync();
  } catch { if (currentAction(attempt)) { actionFailed(attempt, 'Chat could not be taken. Refresh its current assignment.'); sync(); } }
}
async function enableNative() {
  if (!('Notification' in window)) { nativeStatus.value = 'Unavailable'; return; }
  if (Notification.permission === 'denied') { nativeStatus.value = 'Blocked by browser permission'; return; }
  permission.value = await Notification.requestPermission();
  if (permission.value !== 'granted') { nativeStatus.value = `Permission ${permission.value}`; return; }
  await setPreference('native', true);
  await registerPush();
}
async function registerPush() {
  if (disposed || pushRegistering || pushClearing || !currentUser.value?.id || !preferences.value.native || permission.value !== 'granted') return;
  if (!navigator.serviceWorker || !window.chatwootConfig.vapidPublicKey) { nativeStatus.value = 'Page notifications available; push unavailable'; return; }
  pushRegistering = true;
  const epoch = pushEpoch; const actor = currentUser.value.id;
  try {
    registration = await navigator.serviceWorker.register('/attention-sw.js', { scope: '/app/' });
    await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription() || await registration.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: window.chatwootConfig.vapidPublicKey });
    const encode = key => btoa(String.fromCharCode(...new Uint8Array(subscription.getKey(key)))).replace(/\+/g, '-').replace(/\//g, '_');
    const legacy = await navigator.serviceWorker.getRegistration('/');
    const legacySubscription = legacy?.active?.scriptURL === `${location.origin}/sw.js` ? await legacy.pushManager.getSubscription() : null;
    const { data } = await axios.post('/api/v1/attention_devices', { device_id: deviceId, legacy_endpoint: legacySubscription?.endpoint,
      subscription: { endpoint: subscription.endpoint, p256dh: encode('p256dh'), auth: encode('auth') } });
    if (epoch !== pushEpoch || currentUser.value?.id !== actor || !preferences.value.native || permission.value !== 'granted') return;
    if (data.actor_id !== actor || typeof data.binding_id !== 'string' || !/^[a-zA-Z0-9-]{16,80}$/.test(data.binding_id) ||
        !Number.isSafeInteger(data.registration_revision) || data.registration_revision <= 0 ||
        typeof data.cleanup_token !== 'string' || data.cleanup_token.length < 32 || data.cleanup_token.length > 4096) throw Error('Invalid push binding identity');
    pushIdentity = { actor_id: actor, binding_id: data.binding_id, registration_revision: data.registration_revision, cleanup_token: data.cleanup_token };
    if (!registration.active) await new Promise((resolve, reject) => {
      const worker = registration.installing || registration.waiting;
      const timeout = setTimeout(() => reject(new Error('Worker activation timeout')), 5000);
      worker?.addEventListener('statechange', () => { if (worker.state === 'activated') { clearTimeout(timeout); resolve(); } });
    });
    if (epoch !== pushEpoch || currentUser.value?.id !== actor || !preferences.value.native || permission.value !== 'granted') return;
    await new Promise((resolve, reject) => {
      const channel = new MessageChannel();
      const timeout = setTimeout(() => { channel.port1.close(); reject(new Error('Worker binding timeout')); }, 5000);
      channel.port1.onmessage = ({ data: result }) => {
        if (result?.bound && result.binding_id === data.binding_id && result.registration_revision === data.registration_revision) { clearTimeout(timeout); channel.port1.close(); resolve(); }
        else if (result?.bound === false) { clearTimeout(timeout); channel.port1.close(); reject(new Error('Worker binding superseded')); }
      };
      registration.active.postMessage({ type: 'ATTENTION_BIND', ...data }, [channel.port2]);
    });
    if (epoch !== pushEpoch || currentUser.value?.id !== actor) return;
    const activated = await axios.post('/api/v1/attention_devices/activate', { device_id: deviceId, binding_id: data.binding_id });
    if (activated.data?.accepted !== true || epoch !== pushEpoch || currentUser.value?.id !== actor) return;
    // Migrate only this browser's legacy root subscription after the dedicated
    // dashboard worker has accepted the new binding. Other devices stay intact.
    if (!data.legacy_account_ids?.length && legacy?.active?.scriptURL === `${location.origin}/sw.js`) {
      const old = await legacy.pushManager.getSubscription();
      if (old && old.endpoint !== subscription.endpoint) {
        await axios.post('/api/v1/attention_devices/retire_legacy', { endpoint: old.endpoint });
        await old.unsubscribe();
      }
      await legacy.unregister();
    }
    if (disposed || epoch !== pushEpoch || currentUser.value?.id !== actor || !preferences.value.native || permission.value !== 'granted') return;
    pushReady = true;
    pushAccounts = [...data.account_ids].sort((a, b) => a - b).join(',');
    pushRenewAt = Math.min(Date.now() + 3600000, Date.parse(data.expires_at) - 86400000);
    nativeStatus.value = 'Push subscription registered; OS display untested';
  } catch (failure) { if (epoch === pushEpoch) { pushReady = false; pushRenewAt = Date.now() + 60000; nativeStatus.value = `Push unavailable: ${failure.name}`; report(`push:${failure.name}`); } }
  finally { pushRegistering = false; }
}
async function clearPush({ discover = true, actorId = currentUser.value?.id } = {}) {
  let owned = pushIdentity; pushIdentity = null;
  pushEpoch += 1; pushReady = false; pushRenewAt = 0; pushAccounts = '';
  pushClearing += 1;
  nativeStatus.value = 'Push identity cleanup pending; other channels remain active';
  try {
  if (!owned && discover && Number.isSafeInteger(actorId) && deviceId) {
    // Reload may not have registered in this component. Discover only through
    // this authenticated client/session, never by adopting a worker's actor.
    try {
      const { data } = await axios.get('/api/v1/attention_devices', { params: { device_id: deviceId }, timeout: 2000 });
      if (data.owned === true && data.actor_id === actorId && /^[a-zA-Z0-9-]{16,80}$/.test(data.binding_id) &&
          Number.isSafeInteger(data.registration_revision) && data.registration_revision >= 0 &&
          typeof data.cleanup_token === 'string' && data.cleanup_token.length >= 32 && data.cleanup_token.length <= 4096) owned = data;
    } catch { /* no untrusted local identity is adopted */ }
  }
  // Clear local identity synchronously before relying on an authenticated DELETE.
  try {
    registration ||= await navigator.serviceWorker?.getRegistration('/app/');
    if (owned && registration?.active) await new Promise(resolve => {
      const channel = new MessageChannel(); const timeout = setTimeout(() => { channel.port1.close(); resolve(); }, 1500);
      channel.port1.onmessage = () => { clearTimeout(timeout); channel.port1.close(); resolve(); };
      registration.active.postMessage({ type: 'ATTENTION_CLEAR', ...owned }, [channel.port2]);
    });
  } catch { /* counts and logout do not depend on worker cleanup */ }
  try { if (deviceId && owned) await axios.delete('/api/v1/attention_devices', { data: { device_id: deviceId, binding_id: owned.binding_id, cleanup_token: owned.cleanup_token }, timeout: 2000 }); } catch { /* live session-bound server validation still applies */ }
  } finally {
    pushClearing -= 1;
    if (!pushClearing && !pushReady) nativeStatus.value = permission.value !== 'granted' ? `Blocked: permission ${permission.value}` :
      (preferences.value.native ? 'Push cleanup attempted; registration pending' : 'Disabled by preference');
  }
}
async function workerNavigation(event) {
  if (event.source?.scriptURL !== `${location.origin}/attention-sw.js`) return;
  const item = event.data;
  if (item?.type !== 'ATTENTION_NAVIGATION_OFFER' || item.protocol !== 2 || !event.ports[0] || item.actor_id !== currentUser.value?.id || !snapshot.value ||
      !Number.isSafeInteger(item.account_id) || !Number.isSafeInteger(item.conversation_id) ||
      (item.queue_notice === true ? item.conversation_id !== 0 : item.conversation_id <= 0) ||
      (item.queue_notice !== undefined && typeof item.queue_notice !== 'boolean') ||
      ![snapshot.value.account_id, ...snapshot.value.background_accounts.map(account => account.account_id)].includes(item.account_id)) {
    event.ports[0]?.postMessage({ navigated: false }); return;
  }
  const actor = currentUser.value?.id;
  const port = event.ports[0];
  // The worker grants one page ownership before routing begins. A delayed offer
  // never navigates after the worker has already opened its fallback window.
  const granted = await new Promise(resolve => {
    const timeout = setTimeout(() => { port.close?.(); resolve(false); }, 1500);
    port.onmessage = ({ data }) => { if (data?.proceed !== true) return; clearTimeout(timeout); resolve(true); };
    port.postMessage({ accepted: true });
  });
  if (!granted || disposed || currentUser.value?.id !== actor || !snapshot.value || snapshot.value.actor_id !== actor ||
      ![snapshot.value.account_id, ...snapshot.value.background_accounts.map(account => account.account_id)].includes(item.account_id)) {
    port.postMessage({ navigated: false }); return;
  }
  try {
    const navigated = await open(item);
    const current = snapshot.value;
    const allowed = !disposed && currentUser.value?.id === actor && item.actor_id === actor &&
      Number(route.params.accountId) === item.account_id &&
      (item.queue_notice === true ? displayedConversationId.value === null : displayedConversationId.value === item.conversation_id) &&
      (!current || (current.actor_id === actor && [current.account_id, ...current.background_accounts.map(account => account.account_id)].includes(item.account_id)));
    port.postMessage({ navigated: navigated === true && !!allowed });
    // open() owns its diagnostic. A stale offer must not write into a new actor.
  } catch { port.postMessage({ navigated: false }); }
}

function testNative() {
  if (permission.value !== 'granted') { nativeStatus.value = `Blocked: permission ${permission.value}`; return; }
  try {
    const notification = new Notification('Support notification test', { body: 'Channel test only. Customer waiting state is unchanged.', tag: `attention:test:${namespace.value}`, silent: true });
    notification.onclick = () => { window.focus(); notification.close(); };
    nativeStatus.value = 'Display requested; inspect Windows to verify';
  } catch (failure) { nativeStatus.value = `Unavailable: ${failure.name}`; report(`native:${failure.name}`); }
}
async function announce(newItems) {
  const scope = captureAttentionScope();
  if (!currentAttentionScope(scope) || !await scope.lease.owner() || !currentAttentionScope(scope)) return false;
  storageStatus.value = scope.lease.storageReady ? 'Available' : 'Unavailable; broadcast fallback active';
  const actionable = newItems.filter(item => item.unseen && !isPresent(item));
  if (!actionable.length) return true;
  // Deduplicate each offered version before choosing a burst sound. A
  // previously announced escalation must not suppress a newly arrived chat.
  const offered = actionable.filter(item => scope.lease.claimEvent(`${item.account_id}:${item.stream_id}:${item.version}`));
  if (!offered.length) return true;
  const first = offered.find(item => ['escalation', 'handoff', 'assignment'].includes(item.reason)) || offered[0];
  announcement.value = `${counts.value.unseen_conversations || 0} conversations have unseen activity. ${counts.value.awaiting_reply_conversations || 0} await a reply.`;
  clearTimeout(toastTimer); toastTimer = setTimeout(() => { announcement.value = ''; }, 7000);
  // Channel promises are independent, and counts were already committed to UI.
  const conversationCount = new Set(offered.map(item => `${item.account_id}:${item.conversation_id}`)).size;
  const attempts = [play(first.reason, false, scope)];
  if (preferences.value.native && permission.value === 'granted' && !pushReady && !window.attentionHost) {
    attempts.push(Promise.resolve().then(() => {
      if (!currentAttentionScope(scope)) return;
      const notification = new Notification('Support work needs attention', { body: `${conversationCount} conversations have new activity.`, tag: `${scope.namespace}:${snapshot.value.account_id}`, silent: true });
      notification.onclick = () => {
        // Delivery belongs to its captured epoch. A later click may cross an
        // account switch, but only with this actor's current authenticated scope.
        if (!disposed && currentUser.value?.id === scope.actor && snapshot.value?.actor_id === scope.actor &&
            namespace.value === scope.namespace && [snapshot.value.account_id, ...snapshot.value.background_accounts.map(account => account.account_id)].includes(first.account_id)) {
          window.focus(); open(first);
        }
        notification.close();
      };
    }).catch(failure => { if (currentAttentionScope(scope)) { nativeStatus.value = `Unavailable: ${failure.name}`; report(`native:${failure.name}`); } }));
  }
  await Promise.allSettled(attempts);
  return currentAttentionScope(scope);
}
async function remind() {
  const scope = captureAttentionScope();
  if (!currentAttentionScope(scope) || !preferences.value.reminders || !await scope.lease.owner() || !currentAttentionScope(scope)) return;
  const active = new Set();
  for (const item of items.value.filter(row => row.unseen && !isPresent(row))) {
    const key = `${item.account_id}:${item.stream_id}:${item.version}`;
    active.add(key);
    const state = reminders.get(key) || { started: Date.now(), repetitions: 0 };
    reminders.set(key, state);
    if (state.repetitions >= 2 || Date.now() - state.started < 30000 * (state.repetitions + 1)) continue;
    state.repetitions += 1;
    if (scope.lease.claimEvent(`${key}:reminder:${state.repetitions}`)) {
      await play(item.reason, false, scope);
      if (!currentAttentionScope(scope)) return;
      break;
    }
  }
  for (const key of reminders.keys()) if (!active.has(key)) reminders.delete(key);
}
function updateAppBadge(count, epoch) {
  if (!navigator.setAppBadge) return;
  badgeTarget = { count, epoch };
  if (badgePending) return;
  badgePending = true;
  const requested = badgeTarget;
  badgeStatus.value = 'Request pending; installed-app display untested';
  // Keep at most one outstanding API call and the latest desired count. A
  // hung native adapter neither holds sync nor grows a per-poll promise list.
  Promise.resolve().then(() => count ? navigator.setAppBadge(count) : navigator.clearAppBadge())
    .then(() => { if (epoch === generation) badgeStatus.value = 'API accepted; installed-app display untested'; })
    .catch(failure => { if (epoch === generation) badgeStatus.value = `Unavailable: ${failure.name}`; })
    .finally(() => { badgePending = false; if (!disposed && badgeTarget !== requested) updateAppBadge(badgeTarget.count, badgeTarget.epoch); });
}
function acceptSnapshot(data, epoch, account) {
    if (disposed || epoch !== generation || data.account_id !== account || data.actor_id !== currentUser.value.id) return;
    if (data.schema !== 1 || !Array.isArray(data.items) || !Array.isArray(data.background_accounts)) throw new Error('Invalid attention response');
    if (data.capabilities) commitCapabilities(data.capabilities, currentUser.value.id);
    attentionState.snapshot = data;
    const authorizedAccounts = [data.account_id, ...data.background_accounts.map(account => account.account_id)].sort((a, b) => a - b).join(',');
    if (pushReady && pushAccounts !== authorizedAccounts) {
      pushReady = false; pushRenewAt = 0;
      nativeStatus.value = 'Account scope changed; renewing push binding';
    }
    lastSync.value = Date.now(); status.value = data.account_id !== Number(route.params.accountId) ? 'Reconciled on another authorized account' : 'Reconciled'; error.value = '';
    // A healthy-looking socket may lose a customer/assignment frame. Refresh
    // native queue metadata through its existing view/filter owner after the
    // authenticated safety sync, even when no realtime callback arrives.
    // Coalescing, transport cancellation and principal fences live in that
    // native action. A failed listener must not interrupt other channels.
    if (data.account_id === Number(route.params.accountId) && attentionEnabled(data.account_id)) {
      const scope = diagnosticScope();
      try { emitter.emit('fetch_conversation_stats'); }
      catch { report('native_metadata_refresh_failed', 'failed', scope); }
      if (!disposed && epoch === generation && data.actor_id === currentUser.value?.id && attentionEnabled(data.account_id)) {
        try { emitter.emit('reconcile_conversation_list', data.queue_revision); }
        catch { report('native_list_refresh_failed', 'failed', scope); }
      }
    }
    if (disposed || epoch !== generation || data.actor_id !== currentUser.value?.id) return;
    // Readiness follows an authenticated committed snapshot. It is a narrow
    // routing handshake, separate from message-read or responsibility state.
    window.attentionHost?.receiverReady?.({ actor_id: data.actor_id, account_id: data.account_id, deployment_id: data.deployment_id, conversation_id: data.account_id === Number(route.params.accountId) ? displayedConversationId.value : null })
      .catch(() => report('native_receiver_unavailable'));
    if (!lease || lastNamespace !== namespace.value) {
      lease?.destroy(); lastNamespace = namespace.value; lease = new WorkstationLease(namespace.value);
      lastObserved.clear();
    }
    const candidates = [...data.items, data.announcement, ...data.background_accounts.map(account => account.announcement)].filter(Boolean);
    const newItems = candidates.filter(item => item.unseen && (!lastObserved.has(`${item.account_id}:${item.stream_id}`) || item.version > lastObserved.get(`${item.account_id}:${item.stream_id}`)));
    tabAttention.set(data.counts.outstanding_conversations);
    updateAppBadge(data.native_count, epoch);
    announce(newItems).then(offered => {
      if (!offered || epoch !== generation) return;
      for (const item of candidates) {
        const key = `${item.account_id}:${item.stream_id}`;
        lastObserved.set(key, Math.max(item.version, lastObserved.get(key) || 0));
      }
      while (lastObserved.size > 256) lastObserved.delete(lastObserved.keys().next().value);
    }).catch(() => { if (epoch === generation && !disposed) report('announcement_failed'); });
    remind().catch(() => { if (epoch === generation && !disposed) report('reminder_failed'); });
    scheduleVisibleReceipt();
    const disabledPush = !preferences.value.native || permission.value !== 'granted' ? `${data.actor_id}:${permission.value}:${preferences.value.native}` : null;
    if (disabledPush && disabledPush !== pushDisabledState) { pushDisabledState = disabledPush; clearPush(); }
    else if (!disabledPush) pushDisabledState = null;
    if (permission.value === 'granted' && preferences.value.native && Date.now() >= pushRenewAt) registerPush();
}
async function sync(force = false) {
  if (disposed || !route.params.accountId || !currentUser.value?.id) return;
  if (pending) { if (force) resyncRequested = true; return; }
  const epoch = generation;
  const account = requestAccount();
  const actorId = currentUser.value.id;
  pending = true;
  requestAbort = new AbortController();
  try {
    probe = crypto.randomUUID();
    const inRouteAccount = account === Number(route.params.accountId);
    const visibleIds = inRouteAccount ? [...document.querySelectorAll('[data-attention-row]')].map(element => Number(element.dataset.attentionRow)) : [];
    if (inRouteAccount && displayedConversationId.value) visibleIds.push(displayedConversationId.value);
    const messageIds = inRouteAccount && displayedConversationId.value ? [...document.querySelectorAll('[data-message-id]')].map(element => Number(element.dataset.messageId)).filter(id => Number.isSafeInteger(id) && id > 0).slice(-200) : [];
    const { data } = await axios.get(endpoint(), { params: { probe, visible_ids: [...new Set(visibleIds)].filter(id => Number.isSafeInteger(id) && id > 0).slice(0, 100), message_ids: [...new Set(messageIds)] }, signal: requestAbort.signal, timeout: 3500 });
    acceptSnapshot(data, epoch, account);
  } catch (failure) {
    if (epoch !== generation) return;
    if ([401, 403, 404].includes(failure.response?.status)) {
      clearPush({ discover: false }); attentionState.snapshot = null; tabAttention.set(0); updateAppBadge(0, epoch); lease?.destroy(); lease = null;
      window.dispatchEvent(new Event('attention:capability-refresh'));
      status.value = 'Access unavailable';
      clearTimeout(readTimer); readKey = null; lastPong.value = null;
      if (Date.now() >= scopeRetryAt) {
        scopeRetryAt = Date.now() + 15000;
        try {
          const recovered = await recoverAccountAttention({ get: (url, options) => axios.get(url, options), actorId,
            rejectedAccount: account, status: failure.response.status, signal: requestAbort.signal,
            cursor: scopeCursor, current: () => !disposed && epoch === generation && currentUser.value?.id === actorId });
          if (recovered && epoch === generation && currentUser.value?.id === actorId) {
            recoveredAccount.value = recovered.accountId;
            acceptSnapshot(recovered.data, epoch, recovered.accountId);
          } else if (epoch === generation && scopeCursor.index > 0) {
            // Continue the next bounded candidate batch; never probe the same
            // first16 forever or call partial scope an empty authorized queue.
            scopeRetryAt = 0; resyncRequested = true;
          }
        } catch { if (epoch === generation) error.value = 'Authorized account recovery is unavailable. Sign in or check connection readiness.'; }
      }
    } else { status.value = 'Disconnected or stale'; error.value = 'Reconciliation failed. Last known work remains visible.'; }
  } finally {
    if (epoch === generation) {
      pending = false;
      if (resyncRequested) { resyncRequested = false; queueMicrotask(() => sync()); }
    }
  }
}
function onSignal(event) {
  if (event.type === 'keydown') {
    if (event.key === 'Escape' && (expanded.value || healthOpen.value)) closePanels(event);
    return;
  }
  if (event.type === 'attention:receipt-error') {
    const detail = event.detail;
    if (detail?.actor_id !== currentUser.value?.id || detail.account_id !== requestAccount() ||
        !Number.isSafeInteger(detail.conversation_id) || detail.conversation_id <= 0) return;
    actionFailed(beginAction('read', `${detail.account_id}:${detail.conversation_id}`),
      'Personal acknowledgment was not saved. Refresh attention and try again; waiting work remains.');
    return;
  }
  if (event.type === 'attention:pong') {
    if (event.detail?.probe === probe && Number(event.detail.account_id) === requestAccount()) lastPong.value = Date.now();
    return;
  }
  if (event.type === 'attention:open') {
    expanded.value = true; healthOpen.value = false;
    const trigger = document.activeElement;
    // Only an explicit keyboard activation of our queue button requests focus.
    // An arriving event or a later user focus change never moves keyboard focus.
    if (event.detail?.focusPanel === true && trigger?.matches('button.attention-queue-badge')) {
      nextTick(() => {
        if (!disposed && expanded.value && !healthOpen.value && document.activeElement === trigger) {
          attentionElement.value?.querySelector('[aria-label="Close attention queue"]')?.focus();
        }
      });
    }
    return;
  }
  if (event.type === 'visibilitychange') permission.value = 'Notification' in window ? Notification.permission : 'unavailable';
  scheduleVisibleReceipt(); sync(true);
}
function reset(identity, previousIdentity) {
  for (const key of Object.keys(actionErrors)) delete actionErrors[key];
  failedActions.clear();
  if (previousIdentity && identity?.split(':')[1] !== previousIdentity.split(':')[1]) { clearPush({ actorId: Number(previousIdentity.split(':')[1]) }); nativeStatus.value = 'Untested'; }
  requestAbort?.abort(); pending = false; resyncRequested = false;
  generation += 1; recoveredAccount.value = null; scopeRetryAt = 0; scopeCursor = {}; attentionState.snapshot = null;
  clearTimeout(toastTimer); announcement.value = ''; sound?.pause(); soundStatus.value = 'Untested';
  lease?.destroy(); lease = null; lastNamespace = null;
  clearTimeout(readTimer); readKey = null; lastPong.value = null;
  lastObserved.clear(); reminders.clear(); readReceipts.clear();
  reported.clear();
  tabAttention?.set(0); updateAppBadge(0, generation); sync();
}
watch(() => `${route.params.accountId}:${currentUser.value?.id}`, reset);
watch(() => route.fullPath, () => { clearTimeout(readTimer); readKey = null; scheduleVisibleReceipt(); sync(true); });
watch(permission, (value, old) => { if (old === 'granted' && value !== 'granted') { clearPush(); nativeStatus.value = `Blocked: permission ${value}`; } });
onMounted(() => {
  disposed = false; deviceId = safeDeviceId(); tabAttention = new TabAttention();
  window.reliableAttentionCleanup = clearPush;
  for (const name of ['attention:changed', 'attention:pong', 'attention:open', 'attention:receipt-error', 'focus', 'pageshow', 'online']) window.addEventListener(name, onSignal);
  document.addEventListener('visibilitychange', onSignal);
  document.addEventListener('keydown', onSignal);
  navigator.serviceWorker?.addEventListener('message', workerNavigation);
  document.addEventListener('scroll', scheduleVisibleReceipt, true);
  timer = setInterval(() => { now.value = Date.now(); sync(); }, 4000);
  if (window.attentionHost?.capabilities) window.attentionHost.capabilities().then(value => { companion.value = value?.taskbarOverlay ?
    (value.notificationRouting === 'blocked' ? 'Windows counts connected; notification click routing blocked. Check host diagnostics.' : 'Windows taskbar host connected; desktop verification separate') : 'Host unavailable'; }).catch(() => { companion.value = 'Host unavailable'; });
  if (window.attentionHost?.onNavigate) nativeNavigationCleanup = window.attentionHost.onNavigate(async item => {
    if (item.actor_id !== currentUser.value?.id || !snapshot.value ||
      ![snapshot.value.account_id, ...snapshot.value.background_accounts.map(account => account.account_id)].includes(item.account_id)) return;
    const nonce = item.activation_nonce;
    if (!/^[a-f0-9]{32}$/.test(nonce) || !Number.isSafeInteger(item.conversation_id) || item.conversation_id <= 0 || nativeNavigationInFlight.has(nonce)) return;
    if (nativeNavigations.has(nonce)) { window.attentionHost.navigationConsumed?.(item).catch(() => report('native_route_receipt_failed')); return; }
    nativeNavigationInFlight.add(nonce);
    try {
      if (!await open(item)) return;
      if (disposed || item.actor_id !== currentUser.value?.id || Number(route.params.accountId) !== item.account_id ||
          displayedConversationId.value !== item.conversation_id) return;
      nativeNavigations.add(nonce);
      while (nativeNavigations.size > 64) nativeNavigations.delete(nativeNavigations.values().next().value);
      // A cross-account route commits a new authenticated snapshot before the
      // main process accepts this receipt. Replayed nonces retry only receipt.
      await sync();
      await window.attentionHost.navigationConsumed?.(item);
    } catch { report('native_route_receipt_failed'); }
    finally { nativeNavigationInFlight.delete(nonce); }
  });
  sync();
});
onUnmounted(() => {
  disposed = true; generation += 1; clearInterval(timer); clearTimeout(readTimer); clearTimeout(toastTimer);
  for (const name of ['attention:changed', 'attention:pong', 'attention:open', 'attention:receipt-error', 'focus', 'pageshow', 'online']) window.removeEventListener(name, onSignal);
  navigator.serviceWorker?.removeEventListener('message', workerNavigation);
  document.removeEventListener('visibilitychange', onSignal); document.removeEventListener('scroll', scheduleVisibleReceipt, true);
  document.removeEventListener('keydown', onSignal);
  lease?.destroy(); tabAttention?.destroy(); sound?.pause(); attentionState.snapshot = null;
  updateAppBadge(0, generation);
  delete window.reliableAttentionCleanup;
  nativeNavigationCleanup?.();
  nativeNavigations.clear(); nativeNavigationInFlight.clear();
});
</script>

<template>
  <section ref="attentionElement" class="reliable-attention" aria-label="Support attention" data-attention="global">
    <p v-if="snapshot && snapshot.account_id !== Number(route.params.accountId)" class="attention-other-account" role="status">Waiting work in another authorized account: {{ snapshot.account_name }}. Your current conversation remains open.</p>
    <div class="attention-bar" :class="{ outstanding: counts.outstanding_conversations }">
      <button class="attention-summary" :aria-expanded="expanded" aria-controls="attention-work-list" @click="togglePanel('work')">
        <span class="attention-symbol" aria-hidden="true">!</span>
        <strong>{{ snapshot ? countLabel(counts.unseen_conversations) : '—' }} unseen conversations</strong>
        <span>{{ snapshot ? countLabel(counts.unseen_messages) : '—' }} new messages</span>
        <strong>{{ snapshot ? countLabel(counts.awaiting_reply_conversations) : '—' }} awaiting reply</strong>
        <span v-if="oldest">Oldest {{ oldest }}</span>
      </button>
      <button :aria-expanded="healthOpen" aria-controls="attention-health" @click="togglePanel('health')">Alert health · {{ status }}</button>
    </div>
    <div v-if="announcement && !expanded && !healthOpen" class="attention-toast" role="status" aria-live="polite">{{ announcement }} <button aria-label="Dismiss announcement" @click="announcement = ''">×</button></div>
    <div v-if="error" class="attention-error" role="status">{{ error }}</div>
    <div v-for="(message, operation) in actionErrors" :key="operation" class="attention-error" role="status" :data-attention-action-error="operation">{{ message }}</div>
    <div v-if="snapshot && !snapshot.health.backfill_complete" class="attention-error" role="status">Recovering earlier work. Counts may increase as historical waiting items are recovered.</div>
    <div v-if="expanded" id="attention-work-list" class="attention-panel">
      <button class="attention-panel-close" aria-label="Close attention queue" @click="closePanels()">Close</button>
      <p v-if="!snapshot">Authoritative work state has not loaded. {{ status }}.</p>
      <template v-else>
        <p>{{ counts.unassigned_waiting_conversations }} unassigned conversations await a reply across your authorized queues. {{ counts.deferred_conversations }} deferred.</p>
        <div v-for="account in snapshot.background_accounts" :key="account.account_id" class="attention-other-account">
          <button type="button" @click="open({ account_id: account.account_id, queue_notice: true })">{{ account.name }}: {{ account.counts.outstanding_conversations }} outstanding conversations</button>
        </div>
        <p v-if="!items.length">No outstanding work in this account.</p>
        <ol class="attention-items">
          <li v-for="item in items" :key="item.stream_id" :data-attention-conversation="item.conversation_id">
            <div class="attention-item-text">
              <button class="attention-open" @click="open(item)">{{ item.account_name }} · {{ item.inbox_name }} · #{{ item.conversation_id }}</button>
              <span><strong v-if="item.unseen">{{ item.unseen_messages }} new messages · </strong>{{ item.reason.replaceAll('_', ' ') }} · {{ item.state.replaceAll('_', ' ') }}<template v-if="item.waiting_since"> · {{ waitingAge(item.waiting_since, now) }}</template></span>
            </div>
            <button v-if="item.assignment === 'unassigned' && item.waiting" @click="take(item)">Take chat</button>
            <button v-if="item.unseen" :aria-label="`Acknowledge presented activity through version ${item.version} for conversation ${item.conversation_id}; customer waiting remains`" @click="acknowledge(item)">Acknowledge</button>
            <span v-else-if="item.waiting" class="attention-waiting-label">Acknowledged · reply still needed</span>
          </li>
        </ol>
        <p v-if="items.length === snapshot.limit">Showing the oldest {{ snapshot.limit }} items. Counts include all authorized work.</p>
      </template>
    </div>
    <div v-if="healthOpen" id="attention-health" class="attention-panel attention-health">
      <button class="attention-panel-close" aria-label="Close alert health" @click="closePanels()">Close</button>
      <dl>
        <div><dt>Authoritative sync</dt><dd>{{ lastSync ? new Date(lastSync).toLocaleTimeString() : 'Untested' }}</dd></div>
        <div><dt>Realtime freshness</dt><dd>{{ socketStatus }}</dd></div>
        <div><dt>Sound</dt><dd>{{ preferences.sound ? soundStatus : 'Muted by you' }}</dd></div>
        <div><dt>Browser notifications</dt><dd>{{ permission }} · {{ nativeStatus }}</dd></div>
        <div><dt>Installed-app badge</dt><dd>{{ badgeStatus }}</dd></div>
        <div><dt>Enhanced Windows client</dt><dd>{{ companion }}</dd></div>
        <div><dt>Tab coordination storage</dt><dd>{{ storageStatus }}</dd></div>
        <div><dt>Server scheduler</dt><dd>{{ snapshot?.health.watchdog_at ? `${waitingAge(snapshot.health.watchdog_at, now)} since heartbeat` : 'Unknown' }}</dd></div>
        <div><dt>Current queue recovery</dt><dd>{{ attentionState.listRecovery?.account === Number(route.params.accountId) && attentionState.listRecovery?.actor === currentUser?.id ? attentionState.listRecovery.status : 'Unknown on this route' }}</dd></div>
        <div><dt>Queue execution</dt><dd>{{ snapshot?.health.queue_heartbeat_at ? `${waitingAge(snapshot.health.queue_heartbeat_at, now)} since heartbeat` : 'Unknown' }}</dd></div>
        <div><dt>Projection failures</dt><dd>{{ snapshot?.health.failed_projection ?? 'Unknown' }}</dd></div>
      </dl>
      <div class="attention-controls">
        <label><input type="checkbox" :checked="preferences.sound" @change="setPreference('sound', $event.target.checked)"> Sound</label>
        <button @click="enableSound">Enable/test sound</button>
        <label>Volume <input type="range" min="0" max="1" step="0.05" :value="preferences.volume" @change="setPreference('volume', Number($event.target.value))"></label>
        <label><input type="checkbox" :checked="preferences.reminders" @change="setPreference('reminders', $event.target.checked)"> Bounded reminders</label>
        <label><input type="checkbox" :checked="preferences.native" @change="setPreference('native', $event.target.checked)"> Native alerts</label>
        <button @click="enableNative">Enable browser notifications</button>
        <button @click="testNative">Test native display</button>
      </div>
      <p>Tests request a channel effect. Browser acceptance does not prove Windows displayed it or a speaker was audible. Counts and waiting work remain available with sound muted and permission denied.</p>
    </div>
  </section>
</template>

<style>
.reliable-attention { position:relative;flex-shrink:0;z-index:45;font:inherit;color:#28313d;background:#f7f8fa;border-bottom:1px solid #d5d9df; }
.attention-bar { display:flex;align-items:center;justify-content:space-between;gap:8px;padding:3px 12px;min-height:48px; }
.attention-bar.outstanding { background:#fff4ed;border-inline-start:4px solid #b42318; }
.reliable-attention button { min-height:44px;min-width:44px;padding:6px 10px;border-radius:6px;cursor:pointer;font:inherit; }
.reliable-attention button:hover { background:#e8edf4; }
.reliable-attention button:focus-visible,.reliable-attention input:focus-visible,.reliable-attention a:focus-visible { outline:3px solid #2563eb;outline-offset:2px; }
.attention-summary { display:flex;align-items:center;gap:12px;flex-wrap:wrap;text-align:start; }
.attention-summary strong { font-weight:700; }
.attention-symbol { display:inline-grid;place-items:center;width:24px;height:24px;border:2px solid currentColor;border-radius:50%;font-weight:800; }
.attention-panel { position:absolute;top:100%;inset-inline:12px;z-index:51;border:1px solid #d5d9df;border-radius:8px;padding:12px 18px;max-height:min(55vh,420px);overflow:auto;background:#f7f8fa;box-shadow:0 4px 16px #0002; }
.attention-panel-close { display:block;margin-inline-start:auto;margin-bottom:8px; }
.attention-panel p { margin:0 0 8px;font-size:12px; }
.attention-items { list-style:none;margin:8px 0 0;padding:0; }
.attention-items li { display:flex;align-items:center;gap:8px;padding:6px 0;border-top:1px solid #d5d9df; }
.attention-item-text { display:flex;flex:1;min-width:0;flex-direction:column; }
.attention-item-text>span { font-size:12px;padding-inline:10px; }
.attention-open { font-weight:700!important;text-align:start;overflow-wrap:anywhere; }
.attention-waiting-label { font-size:12px;max-width:180px; }
.attention-other-account a { display:inline-block;padding:8px 0;text-decoration:underline;min-height:44px; }
.attention-health dl { display:grid;grid-template-columns:repeat(auto-fit,minmax(220px,1fr));gap:10px;margin:0 0 12px; }
.attention-health dt { font-weight:700;font-size:12px; }
.attention-health dd { margin:2px 0 0;font-size:12px; }
.attention-controls { display:flex;align-items:center;flex-wrap:wrap;gap:12px;margin:10px 0; }
.attention-controls label { display:flex;align-items:center;gap:8px;min-height:44px;font-size:12px; }
.attention-toast { position:absolute;top:100%;right:12px;width:min(440px,calc(100vw - 24px));padding:12px;background:#fff;border:1px solid #b42318;border-radius:8px;box-shadow:0 3px 12px #0002;z-index:50; }
.attention-error { padding:6px 18px;color:#8a1c13;font-size:12px; }
.dark .reliable-attention { color:#f1f4f8;background:#20252d;border-color:#454c59; }
.dark .attention-bar.outstanding { background:#382420;border-color:#ff9b8e; }
.dark .attention-panel,.dark .attention-items li { border-color:#454c59; }
.dark .attention-panel { background:#20252d; }
.dark .reliable-attention button:hover { background:#3d4655; }
.dark .reliable-attention button:focus-visible,.dark .reliable-attention input:focus-visible,.dark .reliable-attention a:focus-visible { outline-color:#93c5fd; }
.dark .attention-toast { background:#20252d;border-color:#ff9b8e; }
.dark .attention-error { color:#ffdad6; }
@media(max-width:700px) { .attention-bar { align-items:start;flex-wrap:wrap; }.attention-summary { gap:6px;font-size:12px; }.attention-items li { flex-wrap:wrap; }.attention-item-text { flex-basis:100%; } }
@media(prefers-reduced-motion:reduce) { .reliable-attention * { animation:none!important;transition:none!important; } }
</style>
